import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/context/StoreContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_PAGE = process.env.REACT_APP_RAZORPAY_PAYMENT_URL;
const money = (n) => `₹${n.toLocaleString("en-IN")}`;

export default function Checkout() {
  const { cart, total, clear } = useStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null); // { code, discount, label }
  const [couponMsg, setCouponMsg] = useState("");
  const nav = useNavigate();

  const discount = coupon?.discount || 0;
  const payable = Math.max(0, total - discount);

  const applyCoupon = async () => {
    setCouponMsg("");
    const code = couponCode.trim().toUpperCase();
    if (!code) { setCouponMsg("Enter a code first."); return; }
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code, subtotal: total });
      setCoupon(res.data);
      setCouponMsg(`${res.data.label} applied · you save ₹${res.data.discount.toLocaleString("en-IN")}`);
    } catch (err) {
      setCoupon(null);
      const raw = err?.response?.data?.detail;
      setCouponMsg(typeof raw === "string" ? raw : "Code not valid.");
    }
  };
  const removeCoupon = () => { setCoupon(null); setCouponCode(""); setCouponMsg(""); };

  useEffect(() => {
    if (!cart.length) return;
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => { try { document.body.removeChild(script); } catch (err) { /* removed on unmount race */ } };
  }, [cart.length]);

  if (!cart.length) return (
    <div className="checkout-page empty" data-testid="checkout-empty">
      <h1>Your bag is empty.</h1>
      <button onClick={() => nav("/shop")} className="lux-cta">Browse the collection</button>
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");

    // Open the payment tab SYNCHRONOUSLY inside the click handler so browsers don't block it.
    // We'll redirect this tab after we have the order id.
    let payWindow = null;
    if (RAZORPAY_PAGE) {
      try { payWindow = window.open("about:blank", "_blank"); } catch (err) { payWindow = null; }
    }

    try {
      const customer = Object.fromEntries(new FormData(e.currentTarget));
      const created = await axios.post(`${API}/payments/create-order`, { amount: payable, customer });

      let paymentRef;
      let status;

      if (created.data.mode === "demo" && RAZORPAY_PAGE) {
        // Hosted razorpay.me page — send the customer there, save the order as awaiting_payment.
        paymentRef = `razorpay_page_${Date.now()}`;
        status = "awaiting_payment";
        const payUrl = `${RAZORPAY_PAGE}?amount=${payable}`;
        if (payWindow && !payWindow.closed) {
          payWindow.location.href = payUrl;
        } else {
          // Popup blocked — redirect current tab (order still gets saved below via a fallback path).
          const order = await axios.post(`${API}/orders`, { customer, items: cart, total: payable, subtotal: total, discount, coupon_code: coupon?.code || "", payment_id: paymentRef, payment_status: status });
          clear();
          sessionStorage.setItem("arisha_last_order", JSON.stringify(order.data));
          window.location.href = payUrl;
          return;
        }
      } else if (created.data.mode === "demo") {
        const verified = await axios.post(`${API}/payments/verify`, { razorpay_order_id: created.data.id, razorpay_payment_id: `demo_pay_${Date.now()}`, razorpay_signature: "demo" });
        if (payWindow) payWindow.close();
        paymentRef = verified.data.payment_id;
        status = "paid";
      } else if (window.Razorpay) {
        if (payWindow) payWindow.close(); // SDK handles its own modal
        let verified;
        await new Promise(resolve => {
          const payment = new window.Razorpay({
            key: created.data.key_id, amount: created.data.amount, currency: "INR", order_id: created.data.id,
            name: "Arisha Creations", description: "Ladies garment order",
            prefill: { name: customer.name, email: customer.email, contact: customer.mobile },
            handler: async response => { verified = await axios.post(`${API}/payments/verify`, response); resolve(); },
            modal: { ondismiss: resolve },
          });
          payment.open();
        });
        if (!verified?.data?.verified) { setBusy(false); return; }
        paymentRef = verified.data.payment_id;
        status = "paid";
      }

      if (!paymentRef) { if (payWindow) payWindow.close(); setBusy(false); return; }

      const order = await axios.post(`${API}/orders`, { customer, items: cart, total: payable, subtotal: total, discount, coupon_code: coupon?.code || "", payment_id: paymentRef, payment_status: status });
      clear();
      nav(`/order/${order.data.id}`, { state: order.data, replace: true });
    } catch (err) {
      if (payWindow) { try { payWindow.close(); } catch (_) { /* noop */ } }
      const raw = err?.response?.data?.detail ?? err?.message ?? "Payment could not start. Please try again.";
      const detail = Array.isArray(raw)
        ? raw.map(d => (d?.msg || d?.message || String(d))).join(", ")
        : (typeof raw === "object" ? (raw.msg || raw.message || JSON.stringify(raw)) : String(raw));
      setError(detail);
      setBusy(false);
    }
  };

  return (
    <div className="checkout-page" data-testid="checkout-page">
      <header className="checkout-head">
        <span className="lux-eyebrow dark">Almost yours</span>
        <h1>Delivery details</h1>
      </header>
      <div className="checkout-layout">
        <form onSubmit={submit} className="checkout-form" data-testid="checkout-form">
          <label className="field"><span>Full name</span><input name="name" required data-testid="checkout-name-input" /></label>
          <div className="field-row">
            <label className="field"><span>Mobile</span><input name="mobile" required pattern="[0-9]{10}" inputMode="numeric" data-testid="checkout-mobile-input" /></label>
            <label className="field"><span>Email</span><input name="email" type="email" required data-testid="checkout-email-input" /></label>
          </div>
          <label className="field"><span>Delivery address</span><textarea name="address" required rows={3} data-testid="checkout-address-input" /></label>
          <div className="field-row">
            <label className="field"><span>City</span><input name="city" required data-testid="checkout-city-input" /></label>
            <label className="field"><span>Pincode</span><input name="pincode" required pattern="[0-9]{6}" inputMode="numeric" data-testid="checkout-pincode-input" /></label>
          </div>
          <div className="payment-note">
            <span aria-hidden>◈</span>
            <div><strong>Razorpay secure checkout</strong><small>UPI · Cards · Net banking · Wallets · Pays to G.K. Garments</small></div>
          </div>
          <div className="coupon-block" data-testid="coupon-block">
            {!coupon ? (
              <>
                <p className="lux-label" style={{margin:"0 0 8px"}}>Have a promo code?</p>
                <div className="coupon-input-row">
                  <input value={couponCode} onChange={e=>setCouponCode(e.target.value.toUpperCase())} placeholder="Enter code" data-testid="coupon-input" />
                  <button type="button" onClick={applyCoupon} data-testid="coupon-apply">Apply</button>
                </div>
              </>
            ) : (
              <div className="coupon-applied" data-testid="coupon-applied">
                <div><strong>{coupon.code}</strong><small>{coupon.label}</small></div>
                <button type="button" onClick={removeCoupon} data-testid="coupon-remove">Remove</button>
              </div>
            )}
            {couponMsg && <p className={`coupon-msg ${coupon ? "ok" : "err"}`} data-testid="coupon-msg">{couponMsg}</p>}
          </div>
          {error && <p className="checkout-error" data-testid="checkout-error" role="alert">{error}</p>}
          <button className="lux-cta full" disabled={busy} type="submit" data-testid="razorpay-pay-button">{busy ? "Opening Razorpay…" : `Pay securely · ${money(payable)}`} →</button>
          <small className="demo-note">Payments are processed by Razorpay directly on this page. Your card and UPI details never touch our servers.</small>
        </form>

        <aside className="checkout-summary" data-testid="checkout-summary">
          <h2>Your order</h2>
          {cart.map(i => (
            <div className="sum-line" key={`${i.id}-${i.size}-${i.color || ""}`}>
              <img src={i.image} alt={i.name} />
              <div>
                <p>{i.name}</p>
                <small>Size {i.size}{i.color ? ` · ${i.color}` : ""} · Qty {i.qty}</small>
              </div>
              <strong>{money(i.price * i.qty)}</strong>
            </div>
          ))}
          <div className="row"><span>Subtotal</span><strong>{money(total)}</strong></div>
          {discount > 0 && <div className="row" data-testid="summary-discount"><span>Discount ({coupon.code})</span><strong>− {money(discount)}</strong></div>}
          <div className="row"><span>Shipping</span><strong>{payable >= 2500 ? "Free" : "At checkout"}</strong></div>
          <div className="row total"><span>Total</span><strong>{money(payable)}</strong></div>
        </aside>
      </div>
    </div>
  );
}
